import { createFileRoute, Link } from "@tanstack/react-router";
import { MarkLast, ReadingPage, readingBody, readingEyebrow, readingH1, readingH2, readingLink, readingSection, readingUpdated } from "@/components/public/reading-page";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";
import { canonicalLink, canonicalMeta, ogImageMeta } from "@/lib/seo";
import {
  CONTACT_EMAIL,
  SELLER_COUNTRY_AR,
  SELLER_COUNTRY_EN,
  SELLER_LEGAL_NAME_AR,
  SELLER_LEGAL_NAME_EN,
} from "@/lib/legal/seller";

export const Route = createFileRoute("/_public/terms")({
  head: () => ({
    meta: [
      { title: "الشروط والأحكام — د. سارة النحّاس" },
      { name: "description", content: "شروط استخدام منصّة د. سارة النحّاس والحجوزات والدفع." },
      { property: "og:title", content: "الشروط والأحكام — د. سارة النحّاس" },
      { property: "og:description", content: "شروط استخدام المنصّة والحجوزات والدفع." },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "ar_SY" },
      { property: "og:site_name", content: "د. سارة النحّاس" },
      ...ogImageMeta(),
      ...canonicalMeta("/terms"),
    ],
    links: [canonicalLink("/terms")],
  }),
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: TermsPage,
});

const sections: { title: string; body: string }[] = [
  {
    title: "الجهة المشغّلة",
    body: `تُدار هذه المنصّة ومنتجاتها المدفوعة من قبل ${SELLER_LEGAL_NAME_AR}، مؤسسة فردية مقرّها ${SELLER_COUNTRY_AR}. للتواصل: ${CONTACT_EMAIL}`,
  },
  {
    title: "استخدام الموقع",
    body: "باستخدامك للمنصّة، فأنت توافق على هذه الشروط. المحتوى مخصّص لأولياء الأمور والطلاب لأغراض تربوية وتعليمية.",
  },
  {
    title: "الحجوزات والدفع",
    body: "المراسلة على واتساب أولاً للاتفاق على اليوم والساعة، ثم إرسال طلب الحجز مع استمارة قصيرة عن الطفل، ثم الدفع ورفع الإيصال، ثم تأكيد الجلسة بعد استلام الدفع أو إيصال التحويل، أو نقداً في العيادة يوم الموعد. الأسعار معروضة بالدولار الأمريكي (USD). الدفع من خارج سوريا يتم عبر PayPal، ومن داخل سوريا عبر حوالة شام كاش أو الهرم. تُدار جميع المدفوعات مباشرة مع الجهة المشغّلة. تغيير الموعد والإلغاء والاسترداد تحكمها سياسة الاسترداد والإلغاء.",
  },
  {
    title: "التسليم",
    body: "الجلسات الأونلاين تتم عبر القناة المتّفق عليها معك، والجلسات الحضورية في العيادة على العنوان المذكور في الحجز. تقرير البوصلة الذكية يصل إلى حسابك على المنصّة خلال 24 ساعة من إتمام نموذج التفضيلات.",
  },
  {
    title: "أمان الدفع",
    body: "بيانات البطاقة يتعامل معها مزوّد الدفع (PayPal) مباشرة، ولا نخزّنها نحن أبداً. الموقع كلّه يعمل عبر اتصال مشفّر (HTTPS).",
  },
  {
    title: "قيود الدفع بالبطاقة",
    body: "الدفع بالبطاقة غير متاح للعملاء داخل سوريا؛ هناك نستخدم الطرق المحلية (شام كاش، حوالة الهرم).",
  },
  {
    title: "البوصلة الذكية",
    body: "البوصلة الذكية منتج رقمي مدفوع يُبنى على نتائجك في اختبار البوصلة وعلى إجاباتك في نموذج التفضيلات. تُصاغ مسودة التقرير بمساعدة الذكاء الاصطناعي، ويراجعها فريق د. سارة قبل إرسالها. يصلك التقرير في حسابك على المنصّة خلال 24 ساعة من إتمام نموذج التفضيلات (وليس من لحظة الدفع). التقرير أداة توجيه تعليمية، وليس تشخيصاً نفسياً ولا ضماناً للقبول الجامعي أو لفرص العمل.",
  },
  {
    title: "الاسترداد والإلغاء",
    body: "كل المبيعات نهائية: لا يُسترَدّ المبلغ عند الإلغاء من طرف العميل أو عدم الحضور أو تغيير الرأي، ويشمل ذلك الجلسات المفردة وباقة الـ 4 جلسات والبوصلة الذكية والدورات المدفوعة. الاستثناءات من طرفنا فقط: تغيير الموعد مجاناً حتى 24 ساعة قبل الموعد المؤكَّد، واختيار موعد جديد أو استرداد الجلسة إذا ألغيناها نحن أو ما قدرنا نعقدها، وردّ الدفع المكرَّر أو الخاطئ دائماً، واسترداد البوصلة الذكية كاملاً إذا لم يصل التقرير خلال 72 ساعة من إتمام نموذج التفضيلات. التفاصيل في سياسة الاسترداد والإلغاء والتسليم (/refund)، وهي المرجع عند أي اختلاف. لا يؤثر ذلك على أي حقوق يمنحها القانون المعمول به.",
  },
  {
    title: "الاستخدام غير المشروع",
    body: "لا يجوز استخدام المنصّة لأي غرض مخالف للقانون، أو للاحتيال أو الرسائل المزعجة، أو للتعدّي على حقوق الملكية الفكرية، أو للعبث بأمن المنصّة (برمجيات ضارّة، اختبار ثغرات، سحب بيانات آلي).",
  },
  {
    title: "حقوق الملكية",
    body: "جميع المحتويات (التقارير، المقالات، الاختبارات) ملكيّة فكرية لـ د. سارة النحّاس. لا يجوز إعادة نشرها أو بيعها دون إذن كتابي.",
  },
  {
    title: "مستوى الخدمة",
    body: "نبذل جهدنا لإتاحة المنصّة باستمرار، لكننا لا نضمن عملها دون انقطاع أو دون أخطاء. يجوز لنا تعليق الوصول أو إنهاؤه عند المخالفة الجوهرية للشروط، أو عدم الدفع، أو وجود خطر أمني أو احتيال.",
  },
  {
    title: "إخلاء المسؤولية",
    body: "الخدمات المقدَّمة استشارية تربوية ولا تُعدّ بديلاً عن التشخيص الطبي أو النفسي المتخصّص عند الحاجة إليه.",
  },
  {
    title: "القانون المطبَّق",
    body: `تخضع هذه الشروط لقوانين ${SELLER_COUNTRY_AR}، وأي نزاع يُحلّ ودّياً أولاً ثم قضائياً عند اللزوم.`,
  },
];

const enSections: { title: string; body: string }[] = [
  {
    title: "Who operates this website",
    body: `This website and its paid products are operated by ${SELLER_LEGAL_NAME_EN}, a sole proprietor based in ${SELLER_COUNTRY_EN}. Contact: ${CONTACT_EMAIL}`,
  },
  {
    title: "Use of the website",
    body: "By using this platform you agree to these terms. The content is intended for parents and students, for educational and developmental purposes.",
  },
  {
    title: "Bookings and payment",
    body: "You message us on WhatsApp first to agree the day and time; then you send the booking request with a short form about your child; you pay and upload the receipt; the session is confirmed once payment or a transfer receipt is received, or you pay cash at the clinic on the day. Prices are shown in US dollars (USD). Payments from outside Syria are made through PayPal (PayPal account or card); inside Syria, by Sham Cash or Al-Haram transfer. All payments are handled directly by the operator. Rescheduling, cancellation and refunds are governed by the Refund, Cancellation & Delivery Policy.",
  },
  {
    title: "Delivery",
    body: "Online sessions take place over the channel agreed with you; in-clinic sessions take place at the clinic address given in your booking. The Smart Bousala report is delivered to your account within 24 hours of completing the preferences form.",
  },
  {
    title: "Payment security",
    body: "Card details are handled by the payment provider (PayPal) and are never stored by us. The whole website runs over an encrypted connection (HTTPS).",
  },
  {
    title: "Card payment restriction",
    body: "Card payments are not available to customers in Syria; local methods (Sham Cash, Al-Haram transfer) are used there.",
  },
  {
    title: "Smart Bousala",
    body: "Smart Bousala is a paid digital product built on your Bousala test results and your answers in the preferences form. The draft is written with the help of AI and reviewed by Dr. Sara's team before it is sent. The report appears in your account within 24 hours of completing the preferences form (not from the moment of payment). It is an educational guidance tool, not a psychological diagnosis, and it does not guarantee university admission or employment.",
  },
  {
    title: "Refunds and cancellation",
    body: "All sales are final: there is no refund when a client cancels, misses a session or changes their mind. This applies to single sessions, the 4-session package, Smart Bousala and paid courses. Provider-side exceptions only: free rescheduling up to 24 hours before the confirmed session; a new appointment or a refund of that session if we cancel or cannot hold it; duplicate or erroneous payments are always returned; and a full Smart Bousala refund if the report is not delivered within 72 hours of completing the preferences form. Full details are in the Refund, Cancellation & Delivery Policy (/refund), which prevails if anything differs. This does not affect any rights you have under applicable law.",
  },
  {
    title: "Misuse",
    body: "You must not use the platform unlawfully, for fraud or spam, to infringe intellectual property rights, or to interfere with its security (malware, probing, automated scraping).",
  },
  {
    title: "Intellectual property",
    body: "All content (reports, articles, assessments) is the intellectual property of Dr. Sara Alnahhas. It may not be republished or resold without written permission.",
  },
  {
    title: "Service level",
    body: "We work to keep the platform available, but we do not guarantee uninterrupted or error-free operation. We may suspend or terminate access for material breach of these terms, non-payment, or a security or fraud risk.",
  },
  {
    title: "Disclaimer",
    body: "The services are educational and advisory. They are not a substitute for specialised medical or psychological diagnosis where that is needed.",
  },
  {
    title: "Governing law",
    body: `These terms are governed by the laws of the ${SELLER_COUNTRY_EN}. Disputes are resolved amicably first, and through the courts where necessary.`,
  },
];

function Block({ title, body, reveal = true }: { title: string; body: string; reveal?: boolean }) {
  return (
    <section className={(reveal ? "reveal " : "") + readingSection}>
      <h2 className="font-display text-[23px] font-extrabold leading-snug text-text-primary">{title}</h2>
      <p className={readingBody}>{body}</p>
    </section>
  );
}

function TermsPage() {
  return (
    <ReadingPage
      header={
        <>
            <p className={readingEyebrow}>الشروط</p>
            <h1 className={readingH1}><MarkLast text="الشروط والأحكام" /></h1>
            <p className={readingUpdated}>
              آخر تحديث: تشرين الأول 2026
            </p>
        </>
      }
    >
      <p className="mt-2 text-[14px] text-brown-500">
        اطّلع أيضاً على{" "}
        <Link to="/refund" className="font-bold text-amber-800 underline underline-offset-4">
          سياسة الاسترداد والإلغاء
        </Link>{" "}و{" "}
        <Link to="/disclaimer" className="font-bold text-amber-800 underline underline-offset-4">
          إخلاء المسؤولية
        </Link>
        .
      </p>
      <div className="mt-10 space-y-8">
        {sections.map((s, i) => (
          <Block key={s.title} title={s.title} body={s.body} reveal={i >= 8} />
        ))}
      </div>

      <div dir="ltr" lang="en" className="mt-16 border-t border-hairline-default pt-10 text-start">
        <h2 className="font-display text-[30px] font-extrabold text-text-primary">Terms &amp; Conditions</h2>
        <p className={readingUpdated}>Last updated: October 2026</p>
        <div className="mt-8 space-y-8">
          {enSections.map((s) => (
            <Block key={s.title} title={s.title} body={s.body} />
          ))}
        </div>
      </div>
    </ReadingPage>
  );
}
